import { create } from "zustand";

import type { RequestScope } from "@/types/request-scope";
import { createAuthSession, sessionSchema } from "../schemas/session.schema";
import type { AuthSession, AuthStoreState } from "../types/auth.types";
import { authStorage } from "./auth-storage";

/** Derives the same validated tenant identity used by UI, query keys, and transport. */
function scopeFor(
  session: AuthSession | null,
  generation: number,
): RequestScope | null {
  return session
    ? {
        generation,
        personId: session.user.personId,
        tenantId: session.activeTenantId,
      }
    : null;
}

const initialSession = authStorage.loadSession();

/** Owns the session snapshot only; app composition coordinates transport and cache cleanup. */
export const useAuthStore = create<AuthStoreState>((set, get) => {
  const replaceSession = (session: AuthSession | null) => {
    const generation = get().generation + 1;
    set({ session, generation, requestScope: scopeFor(session, generation) });
  };
  return {
    session: initialSession,
    generation: 0,
    requestScope: scopeFor(initialSession, 0),
    isInitialized: true,
    setSession: (result) => {
      const session = createAuthSession(result);
      authStorage.saveSession(session);
      replaceSession(session);
    },
    // A late profile save must never relabel a different school/session or mint new permissions.
    updateDisplayName: (scope, displayName) => {
      const state = get();
      if (
        !state.session ||
        state.generation !== scope.generation ||
        state.requestScope?.personId !== scope.personId ||
        state.requestScope.tenantId !== scope.tenantId
      )
        return;
      const session = {
        ...state.session,
        user: { ...state.session.user, displayName },
      };
      authStorage.saveSession(session);
      set({ session });
    },
    updateAdminTitle: (scope, title) => {
      const state = get();
      // A response from a previous account or school must never change the current session.
      if (
        !state.session ||
        state.generation !== scope.generation ||
        state.requestScope?.personId !== scope.personId ||
        state.requestScope.tenantId !== scope.tenantId
      )
        return;
      const session = {
        ...state.session,
        user: {
          ...state.session.user,
          tenants: state.session.user.tenants.map((tenant) =>
            tenant.tenantId === scope.tenantId
              ? { ...tenant, adminTitle: title }
              : tenant,
          ),
        },
      };
      authStorage.saveSession(session);
      set({ session });
    },
    clearSession: () => {
      authStorage.clearSession();
      replaceSession(null);
    },
    switchTenant: (tenantId) => {
      const session = get().session;
      if (!session) return;
      if (session.expiresAt <= Date.now()) {
        get().clearSession();
        return;
      }
      if (
        tenantId === session.activeTenantId ||
        !session.user.tenants.some((t) => t.tenantId === tenantId)
      )
        return;
      const updated = { ...session, activeTenantId: tenantId };
      authStorage.saveSession(updated);
      replaceSession(updated);
    },
    // Storage events carry validated sessions and must not write back to other tabs.
    restoreSession: (session) => {
      const parsed = sessionSchema.safeParse(session);
      replaceSession(
        parsed.success && parsed.data.expiresAt > Date.now()
          ? parsed.data
          : null,
      );
    },
  };
});

/** Public session integration contract; feature consumers never reach into the store. */
type SessionSnapshot = Pick<
  AuthStoreState,
  "session" | "generation" | "requestScope"
>;

export const authSession = {
  getSnapshot(): SessionSnapshot {
    const { session, generation, requestScope } = useAuthStore.getState();
    return { session, generation, requestScope };
  },
  subscribe(
    listener: (state: SessionSnapshot, previous: SessionSnapshot) => void,
  ) {
    return useAuthStore.subscribe(listener);
  },
  clearSession: () => useAuthStore.getState().clearSession(),
  restoreSession: (session: AuthSession | null) =>
    useAuthStore.getState().restoreSession(session),
  /** Updates only the local label for the same authenticated request scope. */
  updateDisplayName: (scope: RequestScope, displayName: string) =>
    useAuthStore.getState().updateDisplayName(scope, displayName),
  updateAdminTitle: (scope: RequestScope, title: string | null) =>
    useAuthStore.getState().updateAdminTitle(scope, title),
  expireIfNeeded(now = Date.now()) {
    const session = useAuthStore.getState().session;
    if (session && session.expiresAt <= now)
      useAuthStore.getState().clearSession();
  },
  isCurrent(scope: RequestScope | null) {
    const state = useAuthStore.getState();
    return (
      scope !== null &&
      state.session !== null &&
      state.session.expiresAt > Date.now() &&
      state.generation === scope.generation &&
      state.requestScope?.personId === scope.personId &&
      state.requestScope.tenantId === scope.tenantId
    );
  },
};
